import React from "react";
import { CloseCircleOutlined } from "@ant-design/icons";
import { Select } from "antd";
import { IUserPortfoGroupSelectionRow } from "../../../Interfaces/portfolioGroups";
import { getAllowedSahraAccounts } from "../../../Requests/general";
import { packetErrorHandler } from "../../../Utils/requestUtils";
import { matchesDropdownSearch } from "src/Utils/dropdownSearch";

const { Option, OptGroup } = Select;

interface Props {
  name: string;
  label: string;
  value?: number[];
  onChange: (name: string, value: number[]) => void;
  disabled?: boolean;
  extraLabel?: boolean;
  withTagBox?: boolean;
}

type GroupBucket = {
  key: string;
  label: string;
  userIds: number[];
};

const PortfoGroupUserMultiSelectDropdown: React.FC<Props> = ({
  name,
  label,
  value,
  onChange,
  disabled = false,
  extraLabel,
  withTagBox,
}) => {
  const [rows, setRows] = React.useState<IUserPortfoGroupSelectionRow[]>([]);
  const [searchValue, setSearchValue] = React.useState("");

  React.useEffect(() => {
    getAllowedSahraAccounts()
      .then(setRows)
      .catch(packetErrorHandler);
  }, []);

  const groups = React.useMemo<GroupBucket[]>(() => {
    const map = new Map<string, GroupBucket>();

    rows.forEach((row) => {
      const key = row.groupId != null ? String(row.groupId) : "__no_group__";
      const labelText = row.groupName || "بدون گروه";
      const existing = map.get(key);
      if (existing) {
        existing.userIds.push(row.id);
        return;
      }

      map.set(key, {
        key,
        label: labelText,
        userIds: [row.id],
      });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "fa"),
    );
  }, [rows]);

  const userItems = React.useMemo(
    () =>
      rows.map((row) => ({
        id: row.id,
        name: row.accountName,
        groupName: row.groupName || "بدون گروه",
      })),
    [rows],
  );

  const selectedIds = React.useMemo(() => value ?? [], [value]);

  const selectedIdSet = React.useMemo(
    () => new Set(selectedIds.map((item) => Number(item))),
    [selectedIds],
  );

  const dropdownValue = selectedIds;

  const handleChange = (nextValues: number[] | null | undefined) => {
    onChange(name, Array.from(new Set(nextValues ?? [])).sort((a, b) => a - b));
    setSearchValue("");
  };

  const toggleGroup = (group: GroupBucket) => {
    const nextIds = new Set(selectedIds.map((item) => Number(item)));
    const isFullySelected = group.userIds.every((id) => nextIds.has(id));

    if (isFullySelected) group.userIds.forEach((id) => nextIds.delete(id));
    else group.userIds.forEach((id) => nextIds.add(id));

    onChange(name, Array.from(nextIds).sort((a, b) => a - b));
  };

  const deleteTagHandler = (id: number) => {
    if (!value) return;
    onChange(
      name,
      value.filter((item) => Number(item) !== Number(id)),
    );
  };

  const filteredUsers = userItems.filter((item) => {
    if (!searchValue) return true;
    return (
      matchesDropdownSearch(item.name, searchValue) ||
      matchesDropdownSearch(item.groupName, searchValue)
    );
  });

  return (
    <div className="w-full flex flex-col justify-start items-start gap-2">
      {extraLabel ? <div className="text-xs">{label}</div> : null}

      <div className="relative w-full">
        <Select
          disabled={disabled}
          value={dropdownValue}
          mode="multiple"
          showSearch
          allowClear
          onSearch={setSearchValue}
          placeholder={label}
          style={{ width: "100%" }}
          onChange={handleChange}
          filterOption={false}
          onClear={() => setSearchValue("")}
          tagRender={() => <></>}
          dropdownRender={(menu) => (
            <div className="flex flex-col gap-2 p-2">
              <div className="flex flex-wrap gap-2">
                {groups.map((group) => {
                  const total = group.userIds.length;
                  const selected = group.userIds.filter((id) =>
                    selectedIdSet.has(id),
                  ).length;
                  const active = total > 0 && selected === total;
                  const partial = selected > 0 && selected < total;

                  return (
                    <button
                      key={group.key}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => toggleGroup(group)}
                      disabled={disabled}
                      className={`px-3 py-1 text-xs border rounded whitespace-nowrap transition-colors ${
                        active
                          ? "bg-sky-600 text-white border-sky-600"
                          : partial
                            ? "bg-sky-50 text-sky-700 border-sky-300"
                            : "bg-white text-gray-700 border-gray-300 hover:border-sky-400"
                      } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
                      title={`انتخاب همه اکانت‌های ${group.label}`}
                    >
                      {group.label}
                      <span className="mr-1 text-[10px] opacity-80">
                        ({total})
                      </span>
                    </button>
                  );
                })}
              </div>
              {menu}
            </div>
          )}
        >
          <OptGroup label="اکانت ها">
            {filteredUsers.map((item) => (
              <Option key={item.id} value={item.id}>
                <div className="flex flex-row justify-start items-center gap-2">
                  <div>{item.name}</div>
                  <div className="text-[10px] px-1 py-px rounded bg-gray-100 text-gray-500 whitespace-nowrap">
                    {item.groupName}
                  </div>
                </div>
              </Option>
            ))}
          </OptGroup>
        </Select>

        {selectedIds.length && !searchValue ? (
          <div className="absolute top-1.5 right-2 text-xs pointer-events-none select-none">
            {selectedIds.length} مورد انتخاب شده است
          </div>
        ) : null}
      </div>

      {withTagBox && selectedIds.length ? (
        <div className="px-2 py-1 flex flex-row flex-wrap justify-start items-center gap-3 border-gray-300 rounded border-2 w-full max-h-20 overflow-y-auto">
          {selectedIds.map((selectedId) => {
            const item = userItems.find(
              (candidate) => candidate.id?.toString() === selectedId?.toString(),
            );
            if (!item) return null;

            return (
              <div
                key={selectedId}
                className="p-1 flex flex-row justify-center items-center gap-2 text-xs bg-gray-100 border-[1px] border-gray-300 rounded"
              >
                <div>{item.name}</div>
                {!disabled ? (
                  <div
                    className="text-sky-700 cursor-pointer pb-px"
                    onClick={() => deleteTagHandler(selectedId)}
                  >
                    <CloseCircleOutlined />
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default PortfoGroupUserMultiSelectDropdown;
